import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getReports, updateReportStatus, processReport, ReportListResponse, ReportFilters, Report } from '../services/reportService';
import { getPostDetail, getCommentDetail } from '../services/contentService';
import { getUserDetail } from '../services/userService';

const ReportReview: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'PENDING' | 'REVIEWED' | 'ACTION_TAKEN' | 'REJECTED'>('all');
  const [reasonFilter, setReasonFilter] = useState<'all' | 'spam' | 'abuse' | 'inappropriate' | 'other'>('all');
  const [contentTypeFilter, setContentTypeFilter] = useState<'all' | 'post' | 'comment'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [processModal, setProcessModal] = useState<{ open: boolean; reportId: string | null; action: string | null }>({ open: false, reportId: null, action: null });
  const [processReason, setProcessReason] = useState('');
  const [contentDetailModal, setContentDetailModal] = useState<{ open: boolean; contentId: string | null; contentType: string | null }>({ open: false, contentId: null, contentType: null });
  const [contentDetail, setContentDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userDetailModal, setUserDetailModal] = useState<{ open: boolean; userId: string | null; userType: 'reporter' | 'reported' | null }>({ open: false, userId: null, userType: null });
  const [userDetail, setUserDetail] = useState<any>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userReportDetailModal, setUserReportDetailModal] = useState<{ open: boolean; report: Report | null }>({ open: false, report: null });

  useEffect(() => {
    fetchReports();
  }, [currentPage, statusFilter, reasonFilter, contentTypeFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const filters: ReportFilters = {
        page: currentPage,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        reason: reasonFilter !== 'all' ? reasonFilter : undefined,
        type: contentTypeFilter !== 'all' ? contentTypeFilter : undefined,
        search: searchTerm || undefined
      };

      const response: ReportListResponse = await getReports(filters);
      setReports(response.reports || []);
      
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
      } else {
        setTotalPages(1);
      }
      
      setError(null);
    } catch (err) {
      setError('신고 목록을 불러오는데 실패했습니다.');
      console.error('Reports fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchReports();
  };

  const handleProcessReport = (reportId: string, action: string) => {
    setProcessModal({ open: true, reportId, action });
    setProcessReason('');
  };

  const handleProcessConfirm = async () => {
    if (!processModal.reportId || !processModal.action || !processReason.trim()) {
      alert('처리 사유를 입력해주세요.');
      return;
    }

    try {
      const success = await processReport(processModal.reportId, {
        action: processModal.action as 'approve' | 'reject',
        reason: processReason.trim()
      });
      
      if (success) {
        alert('신고가 성공적으로 처리되었습니다.');
        setProcessModal({ open: false, reportId: null, action: null });
        setProcessReason('');
        fetchReports(); // 목록 새로고침
      }
    } catch (error) {
      console.error('Report process error:', error);
      alert('신고 처리에 실패했습니다.');
    }
  };

  const handleProcessCancel = () => {
    setProcessModal({ open: false, reportId: null, action: null });
    setProcessReason('');
  };

  const handleContentDetailView = async (contentId: string, contentType: string) => {
    try {
      setDetailLoading(true);
      setContentDetailModal({ open: true, contentId, contentType });
      
      let detail;
      if (contentType === 'comment') {
        detail = await getCommentDetail(contentId);
      } else {
        detail = await getPostDetail(contentId);
      }
      
      // 디버깅을 위한 콘솔 로그
      console.log('Content detail response:', detail);
      console.log('Image fields:', {
        imageUrl: detail?.imageUrl,
        imageName: detail?.imageName,
        images: detail?.images,
        attachments: detail?.attachments,
        files: detail?.files
      });
      
      setContentDetail(detail);
    } catch (err) {
      console.error('Content detail fetch error:', err);
      alert('콘텐츠 상세 정보를 불러오는데 실패했습니다.');
      setContentDetailModal({ open: false, contentId: null, contentType: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleContentDetailClose = () => {
    setContentDetailModal({ open: false, contentId: null, contentType: null });
    setContentDetail(null);
  };

  const handleUserDetailView = async (userId: string, userType: 'reporter' | 'reported') => {
    try {
      setUserDetailLoading(true);
      setUserDetailModal({ open: true, userId, userType });
      
      // API 호출 시도
      let detail;
      try {
        detail = await getUserDetail(userId);
      } catch (apiError) {
        // API 실패 시 테스트 데이터 사용
        detail = {
          userName: `사용자 ${userId}`,
          email: `user${userId}@example.com`,
          joinDate: new Date().toISOString(),
          userStatus: 'active',
          totalReports: userType === 'reported' ? 3 : 0,
          pendingReports: userType === 'reported' ? 1 : 0,
          processedReports: userType === 'reported' ? 2 : 0,
          penaltyInfo: userType === 'reported' ? {
            penaltyLevel: 2,
            currentRestriction: '댓글 작성 제한',
            restrictedUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            restrictionReason: '부적절한 댓글 작성'
          } : null,
          activityInfo: {
            totalPosts: 15,
            totalComments: 42,
            totalReviews: 8,
            lastActivityDate: new Date().toISOString(),
            reportedPosts: userType === 'reported' ? 2 : 0,
            reportedComments: userType === 'reported' ? 3 : 0
          },
          recentReports: userType === 'reported' ? [
            {
              contentTitle: '부적절한 댓글',
              reporterName: '신고자1',
              reason: '욕설/폭력',
              status: '처리완료',
              createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
            }
          ] : [],
          penaltyHistory: userType === 'reported' ? [
            {
              action: '댓글 작성 제한',
              reason: '부적절한 댓글 작성',
              createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
              expiry: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
            }
          ] : []
        };
      }
      
      setUserDetail(detail);
    } catch (err) {
      console.error('User detail fetch error:', err);
      alert('사용자 상세 정보를 불러오는데 실패했습니다.');
      setUserDetailModal({ open: false, userId: null, userType: null });
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleUserDetailClose = () => {
    setUserDetailModal({ open: false, userId: null, userType: null });
    setUserDetail(null);
  };

  const handleUserReportDetailView = (report: Report) => {
    setUserReportDetailModal({ open: true, report });
  };

  const handleUserReportDetailClose = () => {
    setUserReportDetailModal({ open: false, report: null });
  };

  const handleStatusChange = async (reportId: string, newStatus: string) => {
    try {
      const success = await updateReportStatus(reportId, newStatus);
      if (success) {
        // 상태 업데이트 성공 시 목록 새로고침
        fetchReports();
      }
    } catch (error) {
      // 에러 처리
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#ffc107';
      case 'REVIEWED': return '#17a2b8';
      case 'ACTION_TAKEN': return '#28a745';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING': return '접수됨/대기중';
      case 'REVIEWED': return '검토 완료';
      case 'ACTION_TAKEN': return '조치 완료';
      case 'REJECTED': return '신고 반려';
      default: return status || '알 수 없음';
    }
  };

  const getReasonText = (reason: string) => {
    const reasonMap: { [key: string]: string } = {
      'spam': '스팸',
      'abuse': '욕설/비방',
      'inappropriate': '부적절한 콘텐츠',
      'other': '기타'
    };
    return reasonMap[reason] || reason;
  };

  const getContentTypeText = (type: string) => {
    if (!type) return '사용자 신고';
    const typeMap: { [key: string]: string } = {
      'post': '게시물',
      'comment': '댓글'
    };
    return typeMap[type] || type;
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>
          <LoadingIcon>🍊</LoadingIcon>
          신고 목록을 불러오는 중...
        </LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={fetchReports}>
          다시 시도
        </RetryButton>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>신고 목록 관리</Title>
        <Subtitle>신고된 모든 콘텐츠와 사용자의 목록을 확인하고 관리하세요</Subtitle>
      </Header>

      <FilterSection>
        <FilterGroup>
          <FilterLabel>상태</FilterLabel>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'PENDING' | 'REVIEWED' | 'ACTION_TAKEN' | 'REJECTED')}
          >
            <option value="all">전체</option>
            <option value="PENDING">접수됨/대기중</option>
            <option value="REVIEWED">검토 완료</option>
            <option value="ACTION_TAKEN">조치 완료</option>
            <option value="REJECTED">신고 반려</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>신고 사유</FilterLabel>
          <FilterSelect
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value as 'all' | 'spam' | 'abuse' | 'inappropriate' | 'other')}
          >
            <option value="all">전체</option>
            <option value="spam">스팸</option>
            <option value="abuse">욕설/비방</option>
            <option value="inappropriate">부적절한 콘텐츠</option>
            <option value="other">기타</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>콘텐츠 유형</FilterLabel>
          <FilterSelect
            value={contentTypeFilter}
            onChange={(e) => setContentTypeFilter(e.target.value as 'all' | 'post' | 'comment')}
          >
            <option value="all">전체</option>
            <option value="post">게시물</option>
            <option value="comment">댓글</option>
          </FilterSelect>
        </FilterGroup>

        <SearchGroup>
          <SearchInput
            type="text"
            placeholder="신고자, 피신고자 또는 콘텐츠 제목 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <SearchButton onClick={handleSearch}>
            검색
          </SearchButton>
        </SearchGroup>
      </FilterSection>

      <ReportList>
        {reports.length > 0 ? (
          reports.map((report) => (
                                     <ReportItem 
              key={report.id} 
              onClick={() => {
                const isContentReport = (r: Report) => {
                  const ct = (r.contentType || '').toLowerCase();
                  const isContentType = ct === 'post' || ct === 'comment';
                  const hasValidContentId =
                    r.contentId !== null &&
                    r.contentId !== undefined &&
                    String(r.contentId).trim() !== '' &&
                    String(r.contentId).toLowerCase() !== 'null' &&
                    String(r.contentId).toLowerCase() !== 'undefined' &&
                    String(r.contentId) !== '0'; // 백엔드가 0을 넣는 케이스 방지용(필요 시 유지/삭제)
                
                  return isContentType && hasValidContentId;
                };
                
                if (isContentReport(report)) {
                  handleContentDetailView(String(report.contentId), report.contentType);
                } else if (report.reportedUserId) {
                  handleUserDetailView(report.reportedUserId, 'reported');
                } else {
                  // 콘텐츠도, 유저도 아닌 신고(순수 사용자 신고 상세를 보여주고 싶다면)
                  handleUserReportDetailView(report);
                }
              }}
              $clickable={!!report.contentId || !!report.reportedUserId}
            >
               <ReportHeader>
                <ReportInfo>
                  <ReportTitle>
                    [{getContentTypeText(report.contentType)}] {report.contentTitle || report.reportedUserName}
                  </ReportTitle>
                  <ReportMeta>
                    <StatusBadge color={getStatusColor(report.status)}>
                      {getStatusText(report.status)}
                    </StatusBadge>
                    <ReasonBadge>{getReasonText(report.reason)}</ReasonBadge>
                    <ContentTypeBadge>{getContentTypeText(report.contentType)}</ContentTypeBadge>
                    <TimeStamp>{new Date(report.createdAt).toLocaleString('ko-KR')}</TimeStamp>
                  </ReportMeta>
                  <ReportDetails>
                                                                                 <DetailItem>
                      <DetailLabel>신고자:</DetailLabel>
                      <ClickableUserName onClick={(e) => { e.stopPropagation(); handleUserDetailView(report.reporterId, 'reporter'); }}>
                        {report.reporterName}
                      </ClickableUserName>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>피신고자:</DetailLabel>
                      <ClickableUserName onClick={(e) => { e.stopPropagation(); handleUserDetailView(report.reportedUserId, 'reported'); }}>
                        {report.reportedUserName}
                      </ClickableUserName>
                    </DetailItem>
                    {report.contentTitle && (
                      <DetailItem>
                        <DetailLabel>콘텐츠:</DetailLabel>
                        <DetailValue>{report.contentTitle}</DetailValue>
                      </DetailItem>
                    )}

                  </ReportDetails>
                </ReportInfo>
                                 <ActionButtons onClick={(e) => e.stopPropagation()}>
                   <ActionButton
                     onClick={() => handleProcessReport(report.id, 'approve')}
                     disabled={report.status === 'ACTION_TAKEN' || report.status === 'REJECTED'}
                     $primary
                   >
                     승인
                   </ActionButton>
                   <ActionButton
                     onClick={() => handleProcessReport(report.id, 'reject')}
                     disabled={report.status === 'ACTION_TAKEN' || report.status === 'REJECTED'}
                     $secondary
                   >
                     반려
                   </ActionButton>
                 </ActionButtons>
              </ReportHeader>
              
              <ReportDescription>
                {report.detailReason || '신고 내용이 없습니다.'}
              </ReportDescription>
            </ReportItem>
          ))
        ) : (
          <EmptyMessage>
            <EmptyIcon>📝</EmptyIcon>
            신고 내역이 없습니다.
          </EmptyMessage>
                 )}
       </ReportList>

       {/* 신고 처리 모달 */}
       {processModal.open && (
         <ModalOverlay onClick={handleProcessCancel}>
           <ModalContent onClick={(e) => e.stopPropagation()}>
             <ModalHeader>
               <ModalTitle>
                 신고 {processModal.action === 'approve' ? '승인' : '반려'}
               </ModalTitle>
               <CloseButton onClick={handleProcessCancel}>&times;</CloseButton>
             </ModalHeader>
             <ModalBody>
               <ModalSection>
                 <ModalLabel>처리 사유:</ModalLabel>
                 <ModalTextarea
                   value={processReason}
                   onChange={(e) => setProcessReason(e.target.value)}
                   placeholder={`신고 ${processModal.action === 'approve' ? '승인' : '반려'} 사유를 입력하세요...`}
                   rows={4}
                 />
               </ModalSection>
               <ModalActions>
                 <ModalButton onClick={handleProcessConfirm} $primary>
                   {processModal.action === 'approve' ? '승인' : '반려'}
                 </ModalButton>
                 <ModalCancelButton onClick={handleProcessCancel}>
                   취소
                 </ModalCancelButton>
               </ModalActions>
             </ModalBody>
           </ModalContent>
         </ModalOverlay>
       )}

       {/* 콘텐츠 상세정보 모달 */}
       {contentDetailModal.open && (
         <ModalOverlay onClick={handleContentDetailClose}>
           <ModalContent onClick={(e) => e.stopPropagation()}>
             <ModalHeader>
               <ModalTitle>
                 {contentDetailModal.contentType === 'comment' ? '댓글' : '게시글'} 상세정보
               </ModalTitle>
               <CloseButton onClick={handleContentDetailClose}>&times;</CloseButton>
             </ModalHeader>
             <ModalBody>
               {detailLoading ? (
                 <LoadingMessage>
                   <LoadingIcon>🍊</LoadingIcon>
                   콘텐츠 정보를 불러오는 중...
                 </LoadingMessage>
               ) : contentDetail ? (
                 <>
                   {/* 게시글 맥락 정보 (댓글인 경우) */}
                   {contentDetail.postMeta && (
                     <PostContextSection>
                       <PostContextLabel>게시글 맥락:</PostContextLabel>
                       <PostContextItem>
                         <PostContextTitle>{contentDetail.postMeta.title}</PostContextTitle>
                         <PostContextAuthor>작성자: {contentDetail.postMeta.authorNickname}</PostContextAuthor>
                         <PostContextDate>작성일: {new Date(contentDetail.postMeta.createdAt).toLocaleString('ko-KR')}</PostContextDate>
                       </PostContextItem>
                     </PostContextSection>
                   )}

                   {/* 콘텐츠 상태 */}
                   <ModalSection>
                     <ModalLabel>상태:</ModalLabel>
                     <span style={{
                       display: 'inline-block',
                       padding: '4px 12px',
                       borderRadius: '15px',
                       fontSize: '0.8rem',
                       fontWeight: '700',
                       textTransform: 'uppercase',
                       letterSpacing: '0.5px',
                       backgroundColor: contentDetail.status === 'visible' ? '#28a745' : 
                                       contentDetail.status === 'hidden' ? '#ffc107' : '#dc3545',
                       color: 'white',
                       boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                     }}>
                       {contentDetail.status === 'visible' ? '표시됨' : 
                        contentDetail.status === 'hidden' ? '숨김' : '삭제됨'}
                     </span>
                   </ModalSection>

                   {/* 제목 (게시글인 경우) */}
                   {contentDetail.title && (
                     <ModalSection>
                       <ModalLabel>제목:</ModalLabel>
                       <ModalDetailValue>{contentDetail.title}</ModalDetailValue>
                     </ModalSection>
                   )}

                   {/* 작성자 */}
                   <ModalSection>
                     <ModalLabel>작성자:</ModalLabel>
                     <ModalDetailValue>{contentDetail.authorNickname || contentDetail.authorName}</ModalDetailValue>
                   </ModalSection>

                   {/* 카테고리 (게시글인 경우) */}
                   {contentDetail.postCategory && (
                     <ModalSection>
                       <ModalLabel>카테고리:</ModalLabel>
                       <ModalDetailValue>{contentDetail.postCategory}</ModalDetailValue>
                     </ModalSection>
                   )}

                   {/* 작성일 */}
                   <ModalSection>
                     <ModalLabel>작성일:</ModalLabel>
                     <ModalDetailValue>{new Date(contentDetail.createdAt).toLocaleString('ko-KR')}</ModalDetailValue>
                   </ModalSection>

                   {/* 내용 */}
                   <ModalSection>
                     <ModalLabel>{contentDetail.postMeta ? '댓글 내용:' : '게시글 내용:'}</ModalLabel>
                     <ModalDetailValue style={{ 
                       whiteSpace: 'pre-wrap', 
                       lineHeight: '1.6',
                       padding: '15px',
                       backgroundColor: '#f8f9fa',
                       borderRadius: '8px',
                       border: '1px solid #e9ecef',
                       maxHeight: '300px',
                       overflowY: 'auto',
                       wordBreak: 'break-word'
                     }}>
                       {contentDetail.content}
                     </ModalDetailValue>
                   </ModalSection>

                   {/* 첨부 이미지 */}
                   {(contentDetail.imageUrl || contentDetail.attachments || contentDetail.files || contentDetail.images) && (
                     <ModalSection>
                       <ModalLabel>첨부 이미지:</ModalLabel>
                       <ModalDetailValue>
                         {/* 단일 이미지 */}
                         {contentDetail.imageUrl && (
                           <div style={{ marginBottom: '15px' }}>
                             <div style={{ marginBottom: '10px' }}>
                               <strong>이미지명:</strong> {contentDetail.imageName || '이미지'}
                             </div>
                             <div style={{ marginBottom: '10px' }}>
                               <img 
                                 src={contentDetail.imageUrl} 
                                 alt={contentDetail.imageName || '콘텐츠 이미지'}
                                 style={{
                                   maxWidth: '100%',
                                   maxHeight: '400px',
                                   borderRadius: '8px',
                                   border: '1px solid #e9ecef',
                                   objectFit: 'contain'
                                 }}
                                 onError={(e) => {
                                   const target = e.currentTarget;
                                   target.style.display = 'none';
                                   const errorDiv = target.nextElementSibling as HTMLElement;
                                   if (errorDiv) {
                                     errorDiv.style.display = 'block';
                                   }
                                 }}
                               />
                               <div style={{ 
                                 display: 'none', 
                                 padding: '20px', 
                                 backgroundColor: '#f8f9fa', 
                                 borderRadius: '8px', 
                                 border: '1px solid #e9ecef',
                                 textAlign: 'center',
                                 color: '#6c757d',
                                 minHeight: '100px',
                                 alignItems: 'center',
                                 justifyContent: 'center'
                               }}>
                                 <div>
                                   <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🖼️</div>
                                   이미지를 불러올 수 없습니다.
                                 </div>
                               </div>
                             </div>
                             <a 
                               href={contentDetail.imageUrl} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               style={{
                                 display: 'inline-block',
                                 padding: '8px 16px',
                                 backgroundColor: '#007bff',
                                 color: 'white',
                                 textDecoration: 'none',
                                 borderRadius: '5px',
                                 fontSize: '0.9rem'
                               }}
                             >
                               새 창에서 보기
                             </a>
                           </div>
                         )}

                         {/* 이미지 배열 */}
                         {contentDetail.images && contentDetail.images.length > 0 && (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                             {contentDetail.images.map((image: any, index: number) => (
                               <div key={index} style={{ marginBottom: '15px' }}>
                                 <div style={{ marginBottom: '10px' }}>
                                   <strong>이미지 {index + 1}:</strong> {image.name || image.fileName || `이미지 ${index + 1}`}
                                 </div>
                                 <div style={{ marginBottom: '10px' }}>
                                   <img 
                                     src={image.url || image.imageUrl || image.fileUrl} 
                                     alt={image.name || image.fileName || `이미지 ${index + 1}`}
                                     style={{
                                       maxWidth: '100%',
                                       maxHeight: '300px',
                                       borderRadius: '8px',
                                       border: '1px solid #e9ecef',
                                       objectFit: 'contain'
                                     }}
                                     onError={(e) => {
                                       const target = e.currentTarget;
                                       target.style.display = 'none';
                                       const errorDiv = target.nextElementSibling as HTMLElement;
                                       if (errorDiv) {
                                         errorDiv.style.display = 'block';
                                       }
                                     }}
                                   />
                                   <div style={{ 
                                     display: 'none', 
                                     padding: '20px', 
                                     backgroundColor: '#f8f9fa', 
                                     borderRadius: '8px', 
                                     border: '1px solid #e9ecef',
                                     textAlign: 'center',
                                     color: '#6c757d',
                                     minHeight: '100px',
                                     alignItems: 'center',
                                     justifyContent: 'center'
                                   }}>
                                     <div>
                                       <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🖼️</div>
                                       이미지를 불러올 수 없습니다.
                                     </div>
                                   </div>
                                 </div>
                                 <a 
                                   href={image.url || image.imageUrl || image.fileUrl} 
                                   target="_blank" 
                                   rel="noopener noreferrer"
                                   style={{
                                     display: 'inline-block',
                                     padding: '6px 12px',
                                     backgroundColor: '#007bff',
                                     color: 'white',
                                     textDecoration: 'none',
                                     borderRadius: '4px',
                                     fontSize: '0.8rem'
                                   }}
                                 >
                                   새 창에서 보기
                                 </a>
                               </div>
                             ))}
                           </div>
                         )}

                         {/* 첨부파일 배열 (이미지만 필터링) */}
                         {contentDetail.attachments && contentDetail.attachments.length > 0 && (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                             {contentDetail.attachments
                               .filter((file: any) => file.type?.startsWith('image/') || file.mimeType?.startsWith('image/'))
                               .map((file: any, index: number) => (
                               <div key={index} style={{ marginBottom: '15px' }}>
                                 <div style={{ marginBottom: '10px' }}>
                                   <strong>이미지 {index + 1}:</strong> {file.name || file.fileName || `이미지 ${index + 1}`}
                                 </div>
                                 <div style={{ marginBottom: '10px' }}>
                                   <img 
                                     src={file.url || file.fileUrl || file.downloadUrl} 
                                     alt={file.name || file.fileName || `이미지 ${index + 1}`}
                                     style={{
                                       maxWidth: '100%',
                                       maxHeight: '300px',
                                       borderRadius: '8px',
                                       border: '1px solid #e9ecef',
                                       objectFit: 'contain'
                                     }}
                                     onError={(e) => {
                                       const target = e.currentTarget;
                                       target.style.display = 'none';
                                       const errorDiv = target.nextElementSibling as HTMLElement;
                                       if (errorDiv) {
                                         errorDiv.style.display = 'block';
                                       }
                                     }}
                                   />
                                   <div style={{ 
                                     display: 'none', 
                                     padding: '20px', 
                                     backgroundColor: '#f8f9fa', 
                                     borderRadius: '8px', 
                                     border: '1px solid #e9ecef',
                                     textAlign: 'center',
                                     color: '#6c757d',
                                     minHeight: '100px',
                                     alignItems: 'center',
                                     justifyContent: 'center'
                                   }}>
                                     <div>
                                       <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🖼️</div>
                                       이미지를 불러올 수 없습니다.
                                     </div>
                                   </div>
                                 </div>
                                 <a 
                                   href={file.url || file.fileUrl || file.downloadUrl} 
                                   target="_blank" 
                                   rel="noopener noreferrer"
                                   style={{
                                     display: 'inline-block',
                                     padding: '6px 12px',
                                     backgroundColor: '#007bff',
                                     color: 'white',
                                     textDecoration: 'none',
                                     borderRadius: '4px',
                                     fontSize: '0.8rem'
                                   }}
                                 >
                                   새 창에서 보기
                                 </a>
                               </div>
                             ))}
                           </div>
                         )}

                         {/* 파일 배열 (이미지만 필터링) */}
                         {contentDetail.files && contentDetail.files.length > 0 && (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                             {contentDetail.files
                               .filter((file: any) => file.type?.startsWith('image/') || file.mimeType?.startsWith('image/'))
                               .map((file: any, index: number) => (
                               <div key={index} style={{ marginBottom: '15px' }}>
                                 <div style={{ marginBottom: '10px' }}>
                                   <strong>이미지 {index + 1}:</strong> {file.name || file.fileName || `이미지 ${index + 1}`}
                                 </div>
                                 <div style={{ marginBottom: '10px' }}>
                                   <img 
                                     src={file.url || file.fileUrl || file.downloadUrl} 
                                     alt={file.name || file.fileName || `이미지 ${index + 1}`}
                                     style={{
                                       maxWidth: '100%',
                                       maxHeight: '300px',
                                       borderRadius: '8px',
                                       border: '1px solid #e9ecef',
                                       objectFit: 'contain'
                                     }}
                                     onError={(e) => {
                                       const target = e.currentTarget;
                                       target.style.display = 'none';
                                       const errorDiv = target.nextElementSibling as HTMLElement;
                                       if (errorDiv) {
                                         errorDiv.style.display = 'block';
                                       }
                                     }}
                                   />
                                   <div style={{ 
                                     display: 'none', 
                                     padding: '20px', 
                                     backgroundColor: '#f8f9fa', 
                                     borderRadius: '8px', 
                                     border: '1px solid #e9ecef',
                                     textAlign: 'center',
                                     color: '#6c757d',
                                     minHeight: '100px',
                                     alignItems: 'center',
                                     justifyContent: 'center'
                                   }}>
                                     <div>
                                       <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🖼️</div>
                                       이미지를 불러올 수 없습니다.
                                     </div>
                                   </div>
                                 </div>
                                 <a 
                                   href={file.url || file.fileUrl || file.downloadUrl} 
                                   target="_blank" 
                                   rel="noopener noreferrer"
                                   style={{
                                     display: 'inline-block',
                                     padding: '6px 12px',
                                     backgroundColor: '#007bff',
                                     color: 'white',
                                     textDecoration: 'none',
                                     borderRadius: '4px',
                                     fontSize: '0.8rem'
                                   }}
                                 >
                                   새 창에서 보기
                                 </a>
                               </div>
                             ))}
                           </div>
                         )}
                       </ModalDetailValue>
                     </ModalSection>
                   )}

                   {/* 이미지 목록 (여러 개인 경우) */}
                   {contentDetail.images && contentDetail.images.length > 0 && (
                     <ModalSection>
                       <ModalLabel>첨부 이미지들:</ModalLabel>
                       <ModalDetailValue>
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                           {contentDetail.images.map((image: any, index: number) => (
                             <div key={index} style={{ marginBottom: '15px' }}>
                               <div style={{ marginBottom: '10px' }}>
                                 <strong>이미지 {index + 1}:</strong> {image.name || `이미지 ${index + 1}`}
                               </div>
                               <div style={{ marginBottom: '10px' }}>
                                 <img 
                                   src={image.url} 
                                   alt={image.name || `이미지 ${index + 1}`}
                                   style={{
                                     maxWidth: '100%',
                                     maxHeight: '300px',
                                     borderRadius: '8px',
                                     border: '1px solid #e9ecef',
                                     objectFit: 'contain'
                                   }}
                                   onError={(e) => {
                                     const target = e.currentTarget;
                                     target.style.display = 'none';
                                     const errorDiv = target.nextElementSibling as HTMLElement;
                                     if (errorDiv) {
                                       errorDiv.style.display = 'block';
                                     }
                                   }}
                                 />
                                 <div style={{ 
                                   display: 'none', 
                                   padding: '20px', 
                                   backgroundColor: '#f8f9fa', 
                                   borderRadius: '8px', 
                                   border: '1px solid #e9ecef',
                                   textAlign: 'center',
                                   color: '#6c757d',
                                   minHeight: '80px',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <div>
                                     <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🖼️</div>
                                     이미지를 불러올 수 없습니다.
                                   </div>
                                 </div>
                               </div>
                               <a 
                                 href={image.url} 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 style={{
                                   display: 'inline-block',
                                   padding: '6px 12px',
                                   backgroundColor: '#007bff',
                                   color: 'white',
                                   textDecoration: 'none',
                                   borderRadius: '4px',
                                   fontSize: '0.8rem'
                                 }}
                               >
                                 새 창에서 보기
                               </a>
                             </div>
                           ))}
                         </div>
                       </ModalDetailValue>
                     </ModalSection>
                   )}

                   {/* 댓글 목록 (게시글인 경우) */}
                   {contentDetail.comments && contentDetail.comments.length > 0 && (
                     <ModalSection>
                       <ModalLabel>댓글 목록:</ModalLabel>
                       <CommentList>
                         {contentDetail.comments.map((comment: any, index: number) => (
                           <CommentItem key={index}>
                             <CommentHeader>
                               <CommentAuthor>{comment.authorNickname}</CommentAuthor>
                               <span style={{
                                 display: 'inline-block',
                                 padding: '2px 8px',
                                 borderRadius: '10px',
                                 fontSize: '0.7rem',
                                 fontWeight: '700',
                                 textTransform: 'uppercase',
                                 letterSpacing: '0.5px',
                                 backgroundColor: comment.status === 'visible' ? '#28a745' : 
                                                 comment.status === 'hidden' ? '#ffc107' : '#dc3545',
                                 color: 'white',
                                 boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
                               }}>
                                 {comment.status === 'visible' ? '표시됨' : 
                                  comment.status === 'hidden' ? '숨김' : '삭제됨'}
                               </span>
                               <CommentDate>{new Date(comment.createdAt).toLocaleString('ko-KR')}</CommentDate>
                             </CommentHeader>
                             <CommentContent>{comment.content}</CommentContent>
                           </CommentItem>
                         ))}
                       </CommentList>
                     </ModalSection>
                   )}
                 </>
               ) : (
                 <ErrorMessage>콘텐츠 정보를 불러올 수 없습니다.</ErrorMessage>
               )}
             </ModalBody>
           </ModalContent>
         </ModalOverlay>
               )}

        {/* 사용자 상세정보 모달 */}
        {userDetailModal.open && (
          <ModalOverlay onClick={handleUserDetailClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  {userDetailModal.userType === 'reporter' ? '신고자 상세정보' : '피신고자 상세정보'}
                </ModalTitle>
                <CloseButton onClick={handleUserDetailClose}>&times;</CloseButton>
              </ModalHeader>
              <ModalBody>
                {userDetailLoading ? (
                  <LoadingMessage>
                    <LoadingIcon>🍊</LoadingIcon>
                    사용자 정보를 불러오는 중...
                  </LoadingMessage>
                ) : userDetail ? (
                  <>
                    {/* 기본 정보 */}
                    <ModalSection>
                      <ModalLabel>사용자 정보:</ModalLabel>
                      <ModalDetailValue>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>이름:</strong> {userDetail.userName}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>이메일:</strong> {userDetail.email}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>가입일:</strong> {new Date(userDetail.joinDate).toLocaleString('ko-KR')}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>상태:</strong> 
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '15px',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            backgroundColor: userDetail.userStatus === 'active' ? '#28a745' : '#dc3545',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            marginLeft: '8px'
                          }}>
                            {userDetail.userStatus === 'active' ? '활성' : '비활성'}
                          </span>
                        </div>
                        {/* 사용자 신고 관련 추가 정보 */}
                        {userDetailModal.userType === 'reported' && (
                          <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
                            <strong>⚠️ 피신고자 정보:</strong> 이 사용자는 신고를 받은 사용자입니다.
                          </div>
                        )}
                        {userDetailModal.userType === 'reporter' && (
                          <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#d1ecf1', borderRadius: '8px', border: '1px solid #bee5eb' }}>
                            <strong>📝 신고자 정보:</strong> 이 사용자는 신고를 한 사용자입니다.
                          </div>
                        )}
                      </ModalDetailValue>
                    </ModalSection>

                    {/* 신고 통계 */}
                    <ModalSection>
                      <ModalLabel>신고 통계:</ModalLabel>
                      <ModalDetailValue>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>총 신고 수:</strong> {userDetail.totalReports}건
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>대기 중:</strong> {userDetail.pendingReports}건
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>처리 완료:</strong> {userDetail.processedReports}건
                        </div>
                      </ModalDetailValue>
                    </ModalSection>

                    {/* 제재 정보 */}
                    {userDetail.penaltyInfo && (
                      <ModalSection>
                        <ModalLabel>제재 정보:</ModalLabel>
                        <ModalDetailValue>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>제재 레벨:</strong> {userDetail.penaltyInfo.penaltyLevel}
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>현재 제재:</strong> {userDetail.penaltyInfo.currentRestriction}
                          </div>
                          {userDetail.penaltyInfo.restrictedUntil && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong>제재 만료일:</strong> {new Date(userDetail.penaltyInfo.restrictedUntil).toLocaleString('ko-KR')}
                            </div>
                          )}
                          {userDetail.penaltyInfo.restrictionReason && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong>제재 사유:</strong> {userDetail.penaltyInfo.restrictionReason}
                            </div>
                          )}
                        </ModalDetailValue>
                      </ModalSection>
                    )}

                    {/* 활동 정보 */}
                    {userDetail.activityInfo && (
                      <ModalSection>
                        <ModalLabel>활동 정보:</ModalLabel>
                        <ModalDetailValue>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>게시글:</strong> {userDetail.activityInfo.totalPosts}개
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>댓글:</strong> {userDetail.activityInfo.totalComments}개
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>리뷰:</strong> {userDetail.activityInfo.totalReviews}개
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>마지막 활동:</strong> {new Date(userDetail.activityInfo.lastActivityDate).toLocaleString('ko-KR')}
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>신고된 게시글:</strong> {userDetail.activityInfo.reportedPosts}개
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>신고된 댓글:</strong> {userDetail.activityInfo.reportedComments}개
                          </div>
                        </ModalDetailValue>
                      </ModalSection>
                    )}

                    {/* 최근 신고 내역 */}
                    {userDetail.recentReports && userDetail.recentReports.length > 0 && (
                      <ModalSection>
                        <ModalLabel>최근 신고 내역:</ModalLabel>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {userDetail.recentReports.map((report: any, index: number) => (
                            <div key={index} style={{
                              padding: '10px',
                              marginBottom: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '5px' }}>
                                {report.contentTitle || '사용자 신고'}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                신고자: {report.reporterName} | 사유: {getReasonText(report.reason)} | 상태: {getStatusText(report.status)}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                {new Date(report.createdAt).toLocaleString('ko-KR')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ModalSection>
                    )}

                    {/* 제재 이력 */}
                    {userDetail.penaltyHistory && userDetail.penaltyHistory.length > 0 && (
                      <ModalSection>
                        <ModalLabel>제재 이력:</ModalLabel>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {userDetail.penaltyHistory.map((penalty: any, index: number) => (
                            <div key={index} style={{
                              padding: '10px',
                              marginBottom: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '5px' }}>
                                {penalty.action}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                사유: {penalty.reason}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                {new Date(penalty.createdAt).toLocaleString('ko-KR')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ModalSection>
                    )}
                  </>
                ) : (
                  <ErrorMessage>사용자 정보를 불러올 수 없습니다.</ErrorMessage>
                )}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* 사용자 신고 상세정보 모달 */}
        {userReportDetailModal.open && userReportDetailModal.report && (
          <ModalOverlay onClick={handleUserReportDetailClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>사용자 신고 상세정보</ModalTitle>
                <CloseButton onClick={handleUserReportDetailClose}>&times;</CloseButton>
              </ModalHeader>
              <ModalBody>
                <ModalSection>
                  <ModalLabel>신고 정보:</ModalLabel>
                  <ModalDetailValue>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>신고 ID:</strong> {userReportDetailModal.report.id}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>신고 사유:</strong> {userReportDetailModal.report.reason}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>신고일:</strong> {new Date(userReportDetailModal.report.createdAt).toLocaleString('ko-KR')}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>상태:</strong> 
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        marginLeft: '8px',
                        backgroundColor: userReportDetailModal.report.status === 'PENDING' ? '#fff3cd' : 
                                       userReportDetailModal.report.status === 'ACTION_TAKEN' ? '#d4edda' : '#f8d7da',
                        color: userReportDetailModal.report.status === 'PENDING' ? '#856404' : 
                               userReportDetailModal.report.status === 'ACTION_TAKEN' ? '#155724' : '#721c24'
                      }}>
                        {userReportDetailModal.report.status === 'PENDING' ? '대기 중' : 
                         userReportDetailModal.report.status === 'ACTION_TAKEN' ? '승인됨' : '거부됨'}
                      </span>
                    </div>
                  </ModalDetailValue>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>신고자 정보:</ModalLabel>
                  <ModalDetailValue>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>이름:</strong> {userReportDetailModal.report.reporterName}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>ID:</strong> {userReportDetailModal.report.reporterId}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <button 
                        onClick={() => {
                          handleUserDetailView(userReportDetailModal.report!.reporterId, 'reporter');
                          handleUserReportDetailClose();
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        신고자 상세정보 보기
                      </button>
                    </div>
                  </ModalDetailValue>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>피신고자 정보:</ModalLabel>
                  <ModalDetailValue>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>이름:</strong> {userReportDetailModal.report.reportedUserName}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>ID:</strong> {userReportDetailModal.report.reportedUserId}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <button 
                        onClick={() => {
                          handleUserDetailView(userReportDetailModal.report!.reportedUserId, 'reported');
                          handleUserReportDetailClose();
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        피신고자 상세정보 보기
                      </button>
                    </div>
                  </ModalDetailValue>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>신고 내용:</ModalLabel>
                  <ModalDetailValue>
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      marginBottom: '15px'
                    }}>
                      {userReportDetailModal.report.detailReason || '신고 내용이 없습니다.'}
                    </div>
                  </ModalDetailValue>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>첨부 이미지:</ModalLabel>
                  <ModalDetailValue>
                    {userReportDetailModal.report.imageUrl ? (
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>이미지명:</strong> {userReportDetailModal.report.imageName || '이미지'}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <img 
                            src={userReportDetailModal.report.imageUrl} 
                            alt={userReportDetailModal.report.imageName || '신고 이미지'}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                              if (nextElement) {
                                nextElement.style.display = 'block';
                              }
                            }}
                          />
                          <div style={{ 
                            display: 'none', 
                            padding: '20px', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '8px', 
                            border: '1px solid #e9ecef',
                            textAlign: 'center',
                            color: '#6c757d'
                          }}>
                            이미지를 불러올 수 없습니다.
                          </div>
                        </div>
                        <a 
                          href={userReportDetailModal.report.imageUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '5px',
                            fontSize: '0.9rem'
                          }}
                        >
                          새 창에서 보기
                        </a>
                      </div>
                    ) : (
                      <div style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        color: '#6c757d',
                        textAlign: 'center'
                      }}>
                        첨부된 이미지가 없습니다.
                      </div>
                    )}
                  </ModalDetailValue>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>처리:</ModalLabel>
                  <ModalDetailValue>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => {
                          handleProcessReport(userReportDetailModal.report!.id, 'approve');
                          handleUserReportDetailClose();
                        }}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        승인
                      </button>
                      <button 
                        onClick={() => {
                          handleProcessReport(userReportDetailModal.report!.id, 'reject');
                          handleUserReportDetailClose();
                        }}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        거부
                      </button>
                    </div>
                  </ModalDetailValue>
                </ModalSection>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {totalPages > 1 && (
        <Pagination>
          <PageButton
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            이전
          </PageButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <PageButton
              key={page}
              onClick={() => setCurrentPage(page)}
              $active={currentPage === page}
            >
              {page}
            </PageButton>
          ))}
          
          <PageButton
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </Pagination>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 40px;
  background-color: #f8f9fa;
  min-height: calc(100vh - 200px);
  font-family: 'Noto Sans KR', sans-serif;
  color: #343a40;

  @media (max-width: 768px) {
    padding: 20px;
  }
  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #343a40;
  margin: 0 0 10px 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #6c757d;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FilterSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f3f4;
  margin-bottom: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  align-items: end;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 25px;
    gap: 20px;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 150px;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  color: #495057;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const SearchGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 300px;

  @media (max-width: 480px) {
    min-width: auto;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const SearchButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
`;

const ReportItem = styled.div<{ $clickable?: boolean }>`
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    transform: ${props => props.$clickable ? 'translateY(-4px)' : 'none'};
    box-shadow: ${props => props.$clickable ? '0 20px 40px rgba(0, 0, 0, 0.15)' : '0 10px 30px rgba(0, 0, 0, 0.1)'};
  }

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 15px;
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const ReportInfo = styled.div`
  flex: 1;
`;

const ReportTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #343a40;
  margin: 0 0 10px 0;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ReportMeta = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const StatusBadge = styled.span<{ color: string }>`
  background: ${props => props.color};
  color: white;
  padding: 6px 14px;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReasonBadge = styled.span`
  background: linear-gradient(135deg, #17a2b8 0%, #20c997 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContentTypeBadge = styled.span`
  background: linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(111, 66, 193, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimeStamp = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
`;

const ReportDetails = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
`;

const DetailValue = styled.span`
  color: #6c757d;
  font-size: 0.85rem;
`;

const ClickableContentTitle = styled.span`
  color: #007bff;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.3s ease;

  &:hover {
    color: #0056b3;
  }
`;

const ClickableUserName = styled.span`
  color: #007bff;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.3s ease;

  &:hover {
    color: #0056b3;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
  }
`;

const ActionButton = styled.button<{ $primary?: boolean; $secondary?: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => {
    if (props.$primary) return '#28a745';
    if (props.$secondary) return '#17a2b8';
    return '#ff6b35';
  }};
  background: white;
  color: ${props => {
    if (props.$primary) return '#28a745';
    if (props.$secondary) return '#17a2b8';
    return '#ff6b35';
  }};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$primary) return '#28a745';
      if (props.$secondary) return '#17a2b8';
      return '#ff6b35';
    }};
    color: white;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
  }
`;

const ReportDescription = styled.p`
  color: #495057;
  line-height: 1.6;
  margin: 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 10px 15px;
  border: 2px solid ${props => props.$active ? '#ff6b35' : '#e9ecef'};
  background: ${props => props.$active ? '#ff6b35' : 'white'};
  color: ${props => props.$active ? 'white' : '#495057'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: #ff6b35;
    background: #ff6b35;
    color: white;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  font-size: 1.2rem;
  color: #6c757d;
`;

const LoadingIcon = styled.span`
  font-size: 3rem;
  margin-bottom: 20px;
  animation: spin 1.5s linear infinite;
  color: #ff6b35;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fdf2f2;
  color: #e74c3c;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: #c0392b;
  }
`;

// 모달 관련 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 0;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: 30px;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ModalSection = styled.div`
  margin-bottom: 25px;
`;

const ModalLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  font-size: 0.95rem;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.$primary ? '#28a745' : '#6c757d'};
  background: ${props => props.$primary ? '#28a745' : 'white'};
  color: ${props => props.$primary ? 'white' : '#6c757d'};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.$primary ? '#218838' : '#6c757d'};
    color: white;
    transform: translateY(-2px);
  }
`;

const ModalCancelButton = styled.button`
  padding: 12px 24px;
  border: 2px solid #dc3545;
  background: white;
  color: #dc3545;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: #dc3545;
    color: white;
    transform: translateY(-2px);
  }
`;

// 콘텐츠 상세정보 모달 관련 스타일 컴포넌트들
const ModalDetailValue = styled.div`
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PostContextSection = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
`;

const PostContextLabel = styled.div`
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  font-size: 0.9rem;
`;

const PostContextItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const PostContextTitle = styled.div`
  font-weight: 600;
  color: #343a40;
  font-size: 0.95rem;
`;

const PostContextAuthor = styled.div`
  color: #6c757d;
  font-size: 0.85rem;
`;

const PostContextDate = styled.div`
  color: #6c757d;
  font-size: 0.85rem;
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const CommentItem = styled.div`
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e9ecef;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
`;

const CommentDate = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
`;

const CommentContent = styled.div`
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

export default ReportReview;
